import { FilterIdsType } from '@app/components/common/FilterSelect/FilterSelect';
import { IInventorysFilterOptions } from '@app/interfaces/inventory';
import { tableDataInit } from '@app/pages/FishingManagement/Inventory/conts';
import { getUpdatedOptions } from '@app/utils/pageFilters';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Dayjs } from 'dayjs';

export interface InventoryFilterState {
  filterOptions: IInventorysFilterOptions;
  shouldTriggerFetch: boolean;
  selectedOrganizationalEntityIds: FilterIdsType;
  selectedWaterbodyIds: number[];
  selectedEmployeeIds: number[];
  selectedTypeIds: number[];
  selectedOrganizationalEntityIdsTemporal: FilterIdsType;
  selectedWaterbodyIdsTemporal: number[];
  selectedEmployeeIdsTemporal: number[];
  selectedTypeIdsTemporal: number[];
  fromPlannedFilter: Dayjs | null | undefined;
  toPlannedFilter: Dayjs | null | undefined;
  fromExecutedFilter: Dayjs | null | undefined;
  toExecutedFilter: Dayjs | null | undefined;
  onlyPlanned: boolean | null;
  onlyExecuted: boolean | null;
}

const initialState: InventoryFilterState = {
  filterOptions: tableDataInit.filterOptions,
  shouldTriggerFetch: true,
  selectedOrganizationalEntityIds: [],
  selectedWaterbodyIds: [],
  selectedEmployeeIds: [],
  selectedTypeIds: [],
  selectedOrganizationalEntityIdsTemporal: [],
  selectedWaterbodyIdsTemporal: [],
  selectedEmployeeIdsTemporal: [],
  selectedTypeIdsTemporal: [],
  fromPlannedFilter: undefined,
  toPlannedFilter: undefined,
  fromExecutedFilter: undefined,
  toExecutedFilter: undefined,
  onlyPlanned: null,
  onlyExecuted: null,
};

const inventoryFiltersSlice = createSlice({
  name: 'inventoryFilters',
  initialState,
  reducers: {
    setFilterOptions(state, action: PayloadAction<IInventorysFilterOptions>) {
      const filterOptions: IInventorysFilterOptions = {
        ...tableDataInit.filterOptions,
        ...action.payload,
        organizationalEntities: action.payload.organizationalEntities ?? [],
        waterbodies: action.payload.waterbodies ?? [],
        employees: action.payload.employees ?? [],
        type: action.payload.type ?? [],
        status: action.payload.status ?? [],
      };
      state.filterOptions = filterOptions;

      const availableOrganizationalEntityIds = getUpdatedOptions(
        state.selectedOrganizationalEntityIdsTemporal ?? [],
        filterOptions.organizationalEntities,
      );
      state.selectedOrganizationalEntityIdsTemporal = availableOrganizationalEntityIds;
      state.selectedOrganizationalEntityIds = availableOrganizationalEntityIds;

      const availableWaterbodyIds = getUpdatedOptions(
        state.selectedWaterbodyIdsTemporal ?? [],
        filterOptions.waterbodies,
      );
      state.selectedWaterbodyIdsTemporal = availableWaterbodyIds as number[];
      state.selectedWaterbodyIds = availableWaterbodyIds as number[];

      const availableEmployeeIds = getUpdatedOptions(state.selectedEmployeeIdsTemporal ?? [], filterOptions.employees);
      state.selectedEmployeeIdsTemporal = availableEmployeeIds as number[];
      state.selectedEmployeeIds = availableEmployeeIds as number[];

      const availableTypeIds = getUpdatedOptions(state.selectedTypeIdsTemporal ?? [], filterOptions.type);
      state.selectedTypeIdsTemporal = availableTypeIds as number[];
      state.selectedTypeIds = availableTypeIds as number[];
    },
    setShouldTriggerFetch(state, action: PayloadAction<boolean>) {
      state.shouldTriggerFetch = action.payload;
    },
    setSelectedOrganizationalEntityIds(state, action: PayloadAction<FilterIdsType>) {
      state.selectedOrganizationalEntityIds = action.payload;
    },
    setSelectedWaterbodyIds(state, action: PayloadAction<number[]>) {
      state.selectedWaterbodyIds = action.payload;
    },
    setSelectedEmployeeIds(state, action: PayloadAction<number[]>) {
      state.selectedEmployeeIds = action.payload;
    },
    setSelectedTypeIds(state, action: PayloadAction<number[]>) {
      state.selectedTypeIds = action.payload;
    },
    setSelectedOrganizationalEntityIdsTemporal(state, action: PayloadAction<FilterIdsType>) {
      state.selectedOrganizationalEntityIdsTemporal = action.payload;
    },
    setSelectedWaterbodyIdsTemporal(state, action: PayloadAction<number[]>) {
      state.selectedWaterbodyIdsTemporal = action.payload;
    },
    setSelectedEmployeeIdsTemporal(state, action: PayloadAction<number[]>) {
      state.selectedEmployeeIdsTemporal = action.payload;
    },
    setSelectedTypeIdsTemporal(state, action: PayloadAction<number[]>) {
      state.selectedTypeIdsTemporal = action.payload;
    },
    setFromPlannedFilter(state, action: PayloadAction<Dayjs | null | undefined>) {
      state.fromPlannedFilter = action.payload;
      state.shouldTriggerFetch = true;
    },
    setToPlannedFilter(state, action: PayloadAction<Dayjs | null | undefined>) {
      state.toPlannedFilter = action.payload;
      state.shouldTriggerFetch = true;
    },
    setFromExecutedFilter(state, action: PayloadAction<Dayjs | null | undefined>) {
      state.fromExecutedFilter = action.payload;
      state.shouldTriggerFetch = true;
    },
    setToExecutedFilter(state, action: PayloadAction<Dayjs | null | undefined>) {
      state.toExecutedFilter = action.payload;
      state.shouldTriggerFetch = true;
    },
    setOnlyPlanned(state, action: PayloadAction<boolean | null>) {
      state.onlyPlanned = action.payload;
      state.shouldTriggerFetch = true;
    },
    setOnlyExecuted(state, action: PayloadAction<boolean | null>) {
      state.onlyExecuted = action.payload;
      state.shouldTriggerFetch = true;
    },
    resetFilters(state) {
      state.selectedOrganizationalEntityIds = initialState.selectedOrganizationalEntityIds;
      state.selectedWaterbodyIds = initialState.selectedWaterbodyIds;
      state.selectedEmployeeIds = initialState.selectedEmployeeIds;
      state.selectedTypeIds = initialState.selectedTypeIds;
      state.selectedOrganizationalEntityIdsTemporal = initialState.selectedOrganizationalEntityIdsTemporal;
      state.selectedWaterbodyIdsTemporal = initialState.selectedWaterbodyIdsTemporal;
      state.selectedEmployeeIdsTemporal = initialState.selectedEmployeeIdsTemporal;
      state.selectedTypeIdsTemporal = initialState.selectedTypeIdsTemporal;
      state.fromPlannedFilter = initialState.fromPlannedFilter;
      state.toPlannedFilter = initialState.toPlannedFilter;
      state.fromExecutedFilter = initialState.fromExecutedFilter;
      state.toExecutedFilter = initialState.toExecutedFilter;
      state.onlyPlanned = initialState.onlyPlanned;
      state.onlyExecuted = initialState.onlyExecuted;
      state.shouldTriggerFetch = true;
    },
  },
});

export const {
  setFilterOptions,
  setShouldTriggerFetch,
  setSelectedOrganizationalEntityIds,
  setSelectedWaterbodyIds,
  setSelectedEmployeeIds,
  setSelectedTypeIds,
  setSelectedOrganizationalEntityIdsTemporal,
  setSelectedWaterbodyIdsTemporal,
  setSelectedEmployeeIdsTemporal,
  setSelectedTypeIdsTemporal,
  setFromPlannedFilter,
  setToPlannedFilter,
  setFromExecutedFilter,
  setToExecutedFilter,
  setOnlyPlanned,
  setOnlyExecuted,
  resetFilters,
} = inventoryFiltersSlice.actions;

export const areFiltersAppliedSelector = (state: { inventoryFilters: InventoryFilterState }) => {
  const {
    selectedOrganizationalEntityIds,
    selectedWaterbodyIds,
    selectedEmployeeIds,
    selectedTypeIds,
    fromPlannedFilter,
    toPlannedFilter,
    fromExecutedFilter,
    toExecutedFilter,
    onlyPlanned,
    onlyExecuted,
  } = state.inventoryFilters;

  return (
    !!(selectedOrganizationalEntityIds ?? []).length ||
    !!(selectedWaterbodyIds ?? []).length ||
    !!selectedEmployeeIds.length ||
    !!selectedTypeIds.length ||
    !!fromPlannedFilter ||
    !!toPlannedFilter ||
    !!fromExecutedFilter ||
    !!toExecutedFilter ||
    !!onlyPlanned ||
    !!onlyExecuted
  );
};

export const activeFiltersCountSelector = (state: { inventoryFilters: InventoryFilterState }) => {
  const {
    selectedOrganizationalEntityIds,
    selectedWaterbodyIds,
    selectedEmployeeIds,
    selectedTypeIds,
    fromPlannedFilter,
    toPlannedFilter,
    fromExecutedFilter,
    toExecutedFilter,
    onlyPlanned,
    onlyExecuted,
  } = state.inventoryFilters;

  let count = 0;
  if ((selectedOrganizationalEntityIds ?? []).length > 0) count++;
  if ((selectedWaterbodyIds ?? []).length > 0) count++;
  if (selectedEmployeeIds.length > 0) count++;
  if (selectedTypeIds.length > 0) count++;
  if (fromPlannedFilter) count++;
  if (toPlannedFilter) count++;
  if (fromExecutedFilter) count++;
  if (toExecutedFilter) count++;
  if (onlyPlanned) count++;
  if (onlyExecuted) count++;

  return count;
};

export const fetchParamsSelector = (state: { inventoryFilters: InventoryFilterState }) => {
  const {
    selectedOrganizationalEntityIds,
    selectedWaterbodyIds,
    selectedEmployeeIds,
    selectedTypeIds,
    fromPlannedFilter,
    toPlannedFilter,
    fromExecutedFilter,
    toExecutedFilter,
    onlyPlanned,
    onlyExecuted,
  } = state.inventoryFilters;

  return {
    selectedOrganizationalEntityIds: selectedOrganizationalEntityIds ?? [],
    selectedWaterbodyIds: selectedWaterbodyIds ?? [],
    selectedEmployeeIds: selectedEmployeeIds ?? [],
    selectedTypeIds: selectedTypeIds ?? [],
    fromPlannedFilter,
    toPlannedFilter,
    fromExecutedFilter,
    toExecutedFilter,
    onlyPlanned,
    onlyExecuted,
  };
};

export const inventoryFiltersActions = inventoryFiltersSlice.actions;

export const inventoryFilters = inventoryFiltersSlice.reducer;
